import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Sparkles, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdatetime, money } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const Panel = ({ title, hint, children, testid }) => (
  <div className="rounded-xl border border-border bg-card p-4 shadow-xs" data-testid={testid}>
    <p className="font-display text-sm font-semibold">{title}</p>
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    <div className="mt-3">{children}</div>
  </div>
);

const Ranked = ({ rows, label, onOpen }) => (
  <div className="space-y-1.5">
    {rows.length === 0 && <p className="text-sm text-muted-foreground">Nothing here.</p>}
    {rows.map((r) => (
      <button key={r.name || r.number} type="button"
        data-testid={`pf-item-${String(r.number || r.name).replace(/\s+/g, "-").toLowerCase()}`}
        onClick={() => onOpen?.(r)}
        className="flex w-full items-center justify-between gap-3 border-b border-border/60 pb-1.5 text-left last:border-0">
        <span className="min-w-0 truncate text-sm">{r.name || r.number}
          {r.customer_name && <span className="block text-xs text-muted-foreground">{r.customer_name}</span>}
        </span>
        <span className="shrink-0 text-right">
          <span className={cn("num block text-sm font-semibold", (r.profit ?? 0) < 0 ? "text-destructive" : "text-foreground")}>
            {money(r.profit ?? r.cost ?? r.employee_cost)}
          </span>
          <span className="num block text-[11px] text-muted-foreground">
            {r.margin !== undefined ? `${r.margin}% ${label || ""}` : label}
          </span>
        </span>
      </button>
    ))}
  </div>
);

export const ProfitDashboard = ({ month, onOpenInvoice }) => {
  const [d, setD] = useState(null);
  const [ai, setAi] = useState(null);
  const [thinking, setThinking] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/profit/dashboard", { params: { month } });
      setD(data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [month]);

  useEffect(() => { setAi(null); load(); }, [load]);

  const review = async (refresh) => {
    setThinking(true);
    try {
      const { data } = await api.get("/profit/insights", { params: { month, refresh } });
      setAi(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setThinking(false); }
  };

  if (!d) return <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const t = d.totals;

  const flagGroups = ai ? [
    ["Missing costs", ai.missing_costs], ["Abnormal expenses", ai.abnormal_expenses],
    ["Thin margins", ai.low_margins], ["Allocation doubts", ai.allocation_doubts],
  ].filter(([, v]) => (v || []).length) : [];

  return (
    <div className="space-y-5" data-testid="profit-dashboard">
      <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-7">
        {[["Revenue", t.revenue], ["Goods", t.cogs], ["Freight", t.freight],
          ["Expenses", t.expenses], ["People", t.employee], ["Gross profit", t.gross_profit],
          ["Net profit", t.profit]].map(([label, value]) => (
            <div key={label} className="rounded-xl border border-border bg-card p-3">
              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
              <p className={cn("num text-lg font-bold", label === "Net profit" && value < 0 && "text-destructive")}
                data-testid={`pf-${label.toLowerCase().replace(/ /g, "-")}`}>{money(value)}</p>
            </div>
          ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {d.invoice_count} invoice(s) &middot; overall margin <b className="num">{t.margin}%</b> &middot;
        revenue counted {d.config.revenue_basis === "net" ? "before GST" : "including GST"} &middot;
        freight spread by {d.config.freight_basis} &middot; {d.config.working_days} working days a month
      </p>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Most profitable customers" testid="pf-customers">
          <Ranked rows={d.customers.slice(0, 8)} label="margin" />
        </Panel>
        <Panel title="Loss-making invoices" testid="pf-loss"
          hint="Tap one to open the cost trail.">
          <Ranked rows={d.loss_invoices} onOpen={(r) => onOpenInvoice?.(r.invoice_id)} />
        </Panel>
        <Panel title="Best invoices" testid="pf-best">
          <Ranked rows={d.best_invoices} onOpen={(r) => onOpenInvoice?.(r.invoice_id)} />
        </Panel>
        <Panel title="Thin margins" testid="pf-thin"
          hint={`Below ${d.config.thin_margin_pct}% after every cost.`}>
          <Ranked rows={d.thin_margin} onOpen={(r) => onOpenInvoice?.(r.invoice_id)} />
        </Panel>
        <Panel title="By product" testid="pf-products">
          <Ranked rows={d.products.slice(0, 8)} label="margin" />
        </Panel>
        <Panel title="By salesperson" testid="pf-salespeople">
          <Ranked rows={d.salespeople} label="margin" />
        </Panel>
        <Panel title="Employee cost charged" testid="pf-employees"
          hint="Days spent with customers plus their share of the month's salary.">
          <div className="space-y-1.5">
            {d.employees.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No salaries on record yet - add them in Staff Management to see people cost.
              </p>
            )}
            {d.employees.map((e) => (
              <div key={e.name} className="flex items-center justify-between gap-3 border-b border-border/60 pb-1.5 last:border-0">
                <span className="text-sm">{e.name}</span>
                <span className="num text-sm">{money(e.cost)}
                  <span className="text-xs text-muted-foreground"> &middot; {e.days} day(s)</span>
                </span>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Service and drone projects" testid="pf-projects"
          hint="Processing and service work, with the time cost charged to that customer.">
          <div className="space-y-1.5">
            {d.projects.length === 0 && <p className="text-sm text-muted-foreground">No projects in this period.</p>}
            {d.projects.slice(0, 8).map((p) => (
              <div key={p.name} className="flex items-center justify-between gap-3 border-b border-border/60 pb-1.5 last:border-0">
                <span className="min-w-0 truncate text-sm">{p.name}
                  <span className="block text-xs text-muted-foreground">
                    {p.customer_name} &middot; {p.hours} h{p.rework ? ` \u00b7 ${p.rework} rework` : ""}
                  </span>
                </span>
                <span className="num shrink-0 text-sm">{money(p.employee_cost)}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel title="Month on month" testid="pf-months">
        <div className="space-y-1.5">
          {d.months.map((m) => (
            <div key={m.name} className="flex items-center justify-between gap-3 border-b border-border/60 pb-1.5 last:border-0">
              <span className="num text-sm">{m.name}</span>
              <span className="num text-xs text-muted-foreground">
                {m.invoices} invoice(s) &middot; revenue {money(m.revenue)} &middot; profit {money(m.profit)} ({m.margin}%)
              </span>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="AI cost review" testid="pf-ai"
        hint="Looks for missing costs, odd expenses, thin margins and allocations that look wrong.">
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="soft" disabled={thinking} data-testid="pf-ai-run" onClick={() => review(!!ai)}>
            {thinking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {ai ? "Review again" : "Review this month"}
          </Button>
          {ai?.generated_at && <span className="text-xs text-muted-foreground">last run {fdatetime(ai.generated_at)}</span>}
        </div>
        {ai && (
          <div className="mt-3 space-y-3 text-sm">
            {ai.headline && <p className="font-medium">{ai.headline}</p>}
            {flagGroups.map(([title, items]) => (
              <div key={title} className="rounded-lg border border-warning/40 bg-warning-soft/40 px-3 py-2">
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-warning">
                  <TriangleAlert className="h-3.5 w-3.5" /> {title}
                </p>
                <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                  {items.map((x, i) => (
                    <li key={i}>
                      <b className="num">{x.invoice}</b> {x.issue}
                      {x.suggestion && <span className="italic"> &rarr; {x.suggestion}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {!!(ai.actions || []).length && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Suggested actions</p>
                <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                  {ai.actions.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </Panel>
    </div>
  );
};
